import { type Instrument, InstrumentState } from '~/models/instruments';

export function useInstrumentStateText(instrument: Readonly<Ref<Instrument | undefined>>) {
  const { t, locale } = useI18n();
  const { differenceInSeconds, addHours, formatDuration, intervalToDuration } = useDateFns();

  const stateText = ref('');
  const showHiddenResultsUntil = ref(false);

  function createHiddenText() {
    const issueDate = new Date(instrument.value!.IssueDate);

    stateText.value = t('instrument-card.data.will-begin-subscription-at-date', {
      date: issueDate.toLocaleString(locale.value, {
        dateStyle: 'medium',
      }),
    });
  }

  function createHiddenUntilText() {
    const hiddenResultsUntil = new Date(instrument.value!.HiddenResultsUntil!);

    stateText.value = t('instrument-card.data.collecting-orders-until', {
      time: hiddenResultsUntil.toLocaleString(locale.value, {
        hour: 'numeric',
        minute: 'numeric',
      }),
    });
  }

  function createInPreparationText() {
    const issueDate = new Date(instrument.value!.IssueDate);

    stateText.value = t('instrument-card.data.will-begin-subscription-at-date-and-time', {
      date: issueDate.toLocaleString(locale.value, {
        dateStyle: 'medium',
      }),
      time: issueDate.toLocaleString(locale.value, {
        hour: 'numeric',
        minute: 'numeric',
      }),
    });
  }

  function createRemainingTimeText() {
    const endOfSubscription = addHours(
      new Date(instrument.value!.EndOfSubscription ?? Date.now()),
      24,
    );
    const diff = differenceInSeconds(new Date(), endOfSubscription);
    if (diff >= 0) {
      stateText.value = t('instrument-card.data.subscription-ended');
    }

    let duration = intervalToDuration({
      start: new Date(),
      end: endOfSubscription,
    });
    duration = {
      months: duration.months ?? 0,
      days: duration.days ?? 0,
      hours: duration.hours ?? 0,
      minutes: duration.minutes ?? 0,
      seconds: duration.seconds ?? 0,
    };
    if (
      duration.months === 0 &&
      duration.days === 0 &&
      duration.hours === 0 &&
      duration.minutes === 0 &&
      duration.seconds! <= 0
    ) {
      stateText.value = t('instrument-card.data.subscription-ended');

      return;
    }

    if (duration.months === 0 && duration.days! < 1) {
      stateText.value = t('instrument-card.data.remains-less-than-day');

      return;
    }

    stateText.value = formatDuration(duration, {
      format: [...(duration.months !== 0 ? ['months'] : []), 'days'],
      locale: { code: locale.value },
      zero: true,
    });
  }

  function maybeStartRefreshTimer() {
    const issueDate = new Date(instrument.value!.IssueDate);
    const secondsDifference = differenceInSeconds(issueDate, new Date());

    // 6 hours in seconds
    if (secondsDifference > 0 && secondsDifference <= 60 * 60 * 6) {
      setInterval(() => {
        const secondsDifference = differenceInSeconds(issueDate, new Date());

        // between 0 and 10 seconds ellapsed from issueDate
        if (secondsDifference <= 0 && secondsDifference >= -10) {
          window.location.reload();
        }
      }, 10_000);
    }
  }

  watch(
    instrument,
    () => {
      if (!instrument.value) {
        return;
      }

      if (instrument.value.State === InstrumentState.Hidden) {
        createHiddenText();

        return;
      }

      if (
        instrument.value.HideResults &&
        instrument.value!.State !== InstrumentState.EvaluatingResults
      ) {
        showHiddenResultsUntil.value = true;
        createHiddenUntilText();

        return;
      }

      if (instrument.value.State === InstrumentState.InPreparation) {
        const hiddenResultsUntil = new Date(instrument.value.HiddenResultsUntil!);
        const diff = differenceInSeconds(new Date(), hiddenResultsUntil);

        if (diff <= -1 && diff >= -15 * 60) {
          showHiddenResultsUntil.value = true;
          createHiddenUntilText();

          return;
        }

        createInPreparationText();

        maybeStartRefreshTimer();
      }

      if (instrument.value.State === InstrumentState.BeingSubscribed) {
        createRemainingTimeText();
      }
    },
    { immediate: true },
  );

  return {
    stateText,
    showHiddenResultsUntil,
  };
}
